import React, { useState, useEffect, useRef, useCallback } from "react";
import safeToast from "utils/safeToast";

// MUI Components
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Button,
  TextField,
  InputAdornment,
  CircularProgress,
  Chip,
  Grid,
  Tabs,
  Tab,
  Divider,
  IconButton,
  Badge,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import PeopleIcon from "@mui/icons-material/People";
import RefreshIcon from "@mui/icons-material/Refresh";
import DeleteIcon from "@mui/icons-material/Delete";
import MessageIcon from "@mui/icons-material/Message";
import ChatIcon from "@mui/icons-material/Chat";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import PrivateChat from "./PrivateChat";

// Services
import friendService from "API/friendService";
import privateChatService from "API/privateChatService";
import { signalRManager } from "services/signalRManager";
import { useSignalR } from "hooks/useSignalR";

// Custom Components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { getFullUrl } from "config/api";
import { decryptText } from "config/encryption";

const FriendRequests = () => {
  const { connection, isConnected, onlineUsers } = useSignalR();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [currentStudentId, setCurrentStudentId] = useState(null);
  const [tabValue, setTabValue] = useState(0); // 0: All Alumni, 1: Pending, 2: Friends
  const [searchQuery, setSearchQuery] = useState("");
  const [alumni, setAlumni] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [sentRequests, setSentRequests] = useState([]);
  const [friends, setFriends] = useState([]);
  const [unreadCounts, setUnreadCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [selectedAlumni, setSelectedAlumni] = useState(null);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [removeFriendDialogOpen, setRemoveFriendDialogOpen] = useState(false);
  const [friendToRemove, setFriendToRemove] = useState(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [selectedFriendForChat, setSelectedFriendForChat] = useState(null);

  // Get current user ID
  useEffect(() => {
    const userDetails = localStorage.getItem("UserLogDetails");
    if (userDetails) {
      try {
        const decrypted = decryptText(userDetails);
        const { StudentId } = JSON.parse(decrypted);
        setCurrentStudentId(StudentId);
      } catch (error) {
        console.error("Error parsing user details:", error);
        try {
          safeToast.error("Failed to load user session. Please log in again.");
        } catch (toastErr) {
          console.error("Toast error:", toastErr);
        }
      }
    }
  }, []);

  // Load data based on active tab
  useEffect(() => {
    if (currentStudentId) {
      loadData();
    }
  }, [tabValue, currentStudentId]);

  // Update unread count for a specific friend
  const updateUnreadCount = useCallback(async (friendId) => {
    if (!friendId || friendId === currentStudentId) return;
    
    try {
      const countResponse = await privateChatService.getUnreadCount(friendId);
      const count = countResponse?.count || countResponse || 0;
      setUnreadCounts((prev) => ({ ...prev, [friendId]: count }));
    } catch (error) {
      console.error(`Error updating unread count for friend ${friendId}:`, error);
      // Silently fail for unread count updates to avoid spam
      // Only show notification for critical errors
      if (error.response?.status === 401 || error.response?.status === 403) {
        try {
          safeToast.error("Session expired. Please log in again.");
        } catch (toastErr) {
          console.error("Toast error:", toastErr);
        }
      }
    }
  }, [currentStudentId]);

  // SignalR event handlers
  useEffect(() => {
    if (!connection || !isConnected) return;

    const handleFriendRequestReceived = async (friendRequest) => {
      try {
        const requesterName = friendRequest?.RequesterName || friendRequest?.requesterName || "Someone";
        safeToast.info(`Friend request from ${requesterName}`);
      } catch (err) {
        console.error("Toast error:", err);
      }
      await loadPendingRequests();
    };

    const handleFriendRequestSent = async (friendRequest) => {
      try {
        safeToast.success("Friend request sent!");
      } catch (err) {
        console.error("Toast error:", err);
      }
      await loadSentRequests();
      if (tabValue === 0) {
        await loadAlumni();
      }
    };

    const handleFriendRequestAccepted = async (friendRequest) => {
      try {
        const receiverName = friendRequest?.ReceiverName || friendRequest?.receiverName || "Someone";
        safeToast.success(`${receiverName} accepted your friend request!`);
      } catch (err) {
        console.error("Toast error:", err);
      }
      await loadFriends();
      await loadSentRequests();
    };

    const handleFriendRequestAcceptedSuccess = async (friendRequest) => {
      console.log("Friend request accepted via SignalR:", friendRequest);
      try {
        safeToast.success("Friend request accepted!");
      } catch (err) {
        console.error("Toast error:", err);
      }
      await loadPendingRequests();
      await loadFriends();
    };

    const handleFriendRequestRejectedSuccess = async () => {
      console.log("Friend request rejected via SignalR");
      await loadPendingRequests();
    };

    const handleFriendRemoved = async (data) => {
      console.log("Friend removed via SignalR:", data);
      try {
        safeToast.info("Friend removed you");
      } catch (err) {
        console.error("Toast error:", err);
      }
      await loadFriends();
    };

    const handleFriendRemovedSuccess = async (data) => {
      console.log("Friend removed successfully via SignalR:", data);
      try {
        safeToast.success("Friend removed");
      } catch (err) {
        console.error("Toast error:", err);
      }
      await loadFriends();
    };

    const handleError = async (errorMessage) => {
      console.log("SignalR Error received:", errorMessage);
      try {
        if (errorMessage) {
          safeToast.error(String(errorMessage));
        } else {
          safeToast.error("An error occurred");
        }
      } catch (err) {
        console.error("Error displaying toast:", err);
      }
    };

    // Handle private messages to update unread counts
    const handleReceivePrivateMessage = async (privateMessage) => {
      if (privateMessage && privateMessage.toStudentId === currentStudentId) {
        // Update unread count for the sender
        const senderId = privateMessage.fromStudentId;
        if (senderId) {
          await updateUnreadCount(senderId);
        }
      }
    };

    const handlePrivateMessageRead = async (readMessage) => {
      if (readMessage && readMessage.fromStudentId === currentStudentId) {
        // Message was read by recipient, update count for them
        const recipientId = readMessage.toStudentId;
        if (recipientId) {
          // Actually, when a message is read, we don't need to update the count for the sender
          // But if the recipient is in our friends list, we should refresh their count
          await updateUnreadCount(recipientId);
        }
      }
    };

    connection.on("FriendRequestReceived", handleFriendRequestReceived);
    connection.on("FriendRequestSent", handleFriendRequestSent);
    connection.on("FriendRequestAccepted", handleFriendRequestAccepted);
    connection.on("FriendRequestAcceptedSuccess", handleFriendRequestAcceptedSuccess);
    connection.on("FriendRequestRejectedSuccess", handleFriendRequestRejectedSuccess);
    connection.on("FriendRemoved", handleFriendRemoved);
    connection.on("FriendRemovedSuccess", handleFriendRemovedSuccess);
    connection.on("ReceivePrivateMessage", handleReceivePrivateMessage);
    connection.on("PrivateMessageRead", handlePrivateMessageRead);
    connection.on("Error", handleError);

    return () => {
      connection.off("FriendRequestReceived", handleFriendRequestReceived);
      connection.off("FriendRequestSent", handleFriendRequestSent);
      connection.off("FriendRequestAccepted", handleFriendRequestAccepted);
      connection.off("FriendRequestAcceptedSuccess", handleFriendRequestAcceptedSuccess);
      connection.off("FriendRequestRejectedSuccess", handleFriendRequestRejectedSuccess);
      connection.off("FriendRemoved", handleFriendRemoved);
      connection.off("FriendRemovedSuccess", handleFriendRemovedSuccess);
      connection.off("ReceivePrivateMessage", handleReceivePrivateMessage);
      connection.off("PrivateMessageRead", handlePrivateMessageRead);
      connection.off("Error", handleError);
    };
  }, [connection, isConnected, tabValue, currentStudentId, updateUnreadCount]);

  const loadData = async () => {
    try {
      if (tabValue === 0) {
        await loadAlumni();
      } else if (tabValue === 1) {
        // Load both requests in parallel but handle errors separately
        await Promise.allSettled([
          loadPendingRequests(),
          loadSentRequests()
        ]);
      } else if (tabValue === 2) {
        await loadFriends();
      }
    } catch (error) {
      console.error("Error in loadData:", error);
      // Individual load functions handle their own errors
    }
  };

  const loadAlumni = async () => {
    setLoading(true);
    try {
      const data = await friendService.getAllAlumni(searchQuery, pageNumber, 20);
      setAlumni(data || []);
    } catch (error) {
      try {
        safeToast.error("Failed to load alumni");
      } catch (toastErr) {
        console.error("Toast error:", toastErr);
      }
      console.error("Error loading alumni:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadPendingRequests = async () => {
    try {
      const data = await friendService.getPendingRequests();
      setPendingRequests(data || []);
    } catch (error) {
      console.error("Error loading pending requests:", error);
      // Only show error if it's not a 404 (not found) or if it's a server error
      if (error.response?.status && error.response.status !== 404) {
        try {
          const errorMsg = error.response?.data?.error || error.message || "Failed to load pending requests";
          safeToast.error(String(errorMsg));
        } catch (toastErr) {
          console.error("Toast error:", toastErr);
        }
      } else {
        // 404 or no data - just set empty array, no error notification
        setPendingRequests([]);
      }
    }
  };

  const loadSentRequests = async () => {
    try {
      const data = await friendService.getSentRequests();
      setSentRequests(data || []);
    } catch (error) {
      console.error("Error loading sent requests:", error);
      // Only show error if it's not a 404 (not found) or if it's a server error
      if (error.response?.status && error.response.status !== 404) {
        try {
          const errorMsg = error.response?.data?.error || error.message || "Failed to load sent requests";
          safeToast.error(String(errorMsg));
        } catch (toastErr) {
          console.error("Toast error:", toastErr);
        }
      } else {
        // 404 or no data - just set empty array, no error notification
        setSentRequests([]);
      }
    }
  };

  const loadFriends = async () => {
    setLoading(true);
    try {
      const data = await friendService.getFriends();
      setFriends(data || []);
      
      // Load unread counts for each friend
      if (data && data.length > 0 && currentStudentId) {
        const counts = {};
        for (const friend of data) {
          // Calculate friendId consistently with renderFriendCard
          const requesterId = friend.RequesterId || friend.requesterId;
          const receiverId = friend.ReceiverId || friend.receiverId;
          const friendId = requesterId === currentStudentId ? receiverId : requesterId;
          
          if (friendId && friendId !== currentStudentId) {
            try {
              const countResponse = await privateChatService.getUnreadCount(friendId);
              const count = countResponse?.count || countResponse || 0;
              counts[friendId] = count;
              console.log(`Unread count for friend ${friendId}: ${count}`);
            } catch (error) {
              console.error(`Error loading unread count for friend ${friendId}:`, error);
              counts[friendId] = 0;
            }
          }
        }
        console.log("All unread counts:", counts);
        setUnreadCounts(counts);
      }
    } catch (error) {
      try {
        safeToast.error("Failed to load friends");
      } catch (toastErr) {
        console.error("Toast error:", toastErr);
      }
      console.error("Error loading friends:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPageNumber(1);
    loadAlumni();
  };

  const handleSendRequest = async (receiverId) => {
    try {
      if (isConnected && connection) {
        await connection.invoke("SendFriendRequest", receiverId);
      } else {
        await friendService.sendFriendRequest(receiverId);
        try {
          safeToast.success("Friend request sent!");
        } catch (toastErr) {
          console.error("Toast error:", toastErr);
        }
        loadAlumni();
      }
    } catch (error) {
      try {
        const errorMsg = error.response?.data?.error || "Failed to send friend request";
        safeToast.error(String(errorMsg));
      } catch (toastErr) {
        console.error("Toast error:", toastErr);
      }
      console.error("Error sending friend request:", error);
    }
  };

  const handleAcceptRequest = async (requestId) => {
    try {
      console.log("handleAcceptRequest called with requestId:", requestId);
      console.log("isConnected:", isConnected, "connection:", connection);
      
      if (isConnected && connection) {
        console.log("Using SignalR to accept friend request");
        await connection.invoke("AcceptFriendRequest", requestId);
        // SignalR will handle the UI update via event handlers
      } else {
        console.log("Using API to accept friend request");
        const result = await friendService.acceptFriendRequest(requestId);
        console.log("API result:", result);
        try {
          safeToast.success("Friend request accepted!");
        } catch (toastErr) {
          console.error("Toast error:", toastErr);
        }
        await loadPendingRequests();
        await loadFriends();
      }
    } catch (error) {
      console.error("Full error object:", error);
      try {
        const errorMsg = error.response?.data?.error || error.message || "Failed to accept friend request";
        safeToast.error(String(errorMsg));
      } catch (toastErr) {
        console.error("Toast error:", toastErr);
      }
      console.error("Error accepting friend request:", error);
    }
  };

  const handleRejectRequest = async (requestId) => {
    try {
      console.log("handleRejectRequest called with requestId:", requestId);
      
      if (isConnected && connection) {
        console.log("Using SignalR to reject friend request");
        await connection.invoke("RejectFriendRequest", requestId);
        // SignalR will handle the UI update via event handlers
      } else {
        console.log("Using API to reject friend request");
        const result = await friendService.rejectFriendRequest(requestId);
        console.log("API result:", result);
        try {
          safeToast.success("Friend request rejected");
        } catch (toastErr) {
          console.error("Toast error:", toastErr);
        }
        await loadPendingRequests();
      }
    } catch (error) {
      console.error("Full error object:", error);
      try {
        const errorMsg = error.response?.data?.error || error.message || "Failed to reject friend request";
        safeToast.error(String(errorMsg));
      } catch (toastErr) {
        console.error("Toast error:", toastErr);
      }
      console.error("Error rejecting friend request:", error);
    }
  };

  const handleCancelRequest = async (requestId) => {
    try {
      console.log("handleCancelRequest called with requestId:", requestId);
      const result = await friendService.cancelFriendRequest(requestId);
      console.log("Cancel API result:", result);
      try {
        safeToast.success("Friend request cancelled");
      } catch (toastErr) {
        console.error("Toast error:", toastErr);
      }
      await loadSentRequests();
      if (tabValue === 0) {
        await loadAlumni();
      }
    } catch (error) {
      console.error("Full error object:", error);
      try {
        const errorMsg = error.response?.data?.error || error.message || "Failed to cancel friend request";
        safeToast.error(String(errorMsg));
      } catch (toastErr) {
        console.error("Toast error:", toastErr);
      }
      console.error("Error cancelling friend request:", error);
    }
  };

  const handleRemoveFriendClick = (friendId, friendName) => {
    setFriendToRemove({ friendId, friendName });
    setRemoveFriendDialogOpen(true);
  };

  const handleConfirmRemoveFriend = async () => {
    if (!friendToRemove) return;
    
    try {
      console.log("handleRemoveFriend called with friendId:", friendToRemove.friendId);
      console.log("friendToRemove object:", friendToRemove);
      
      // Ensure friendId is a number
      const friendId = Number(friendToRemove.friendId);
      if (isNaN(friendId) || friendId <= 0) {
        try {
          safeToast.error("Invalid friend ID");
        } catch (toastErr) {
          console.error("Toast error:", toastErr);
        }
        setRemoveFriendDialogOpen(false);
        setFriendToRemove(null);
        return;
      }
      
      setRemoveFriendDialogOpen(false);
      
      if (isConnected && connection) {
        console.log("Using SignalR to remove friend, friendId:", friendId);
        await connection.invoke("RemoveFriend", friendId);
        // SignalR will handle the UI update via event handlers
      } else {
        console.log("Using API to remove friend");
        const result = await friendService.removeFriend(friendId);
        console.log("Remove friend API result:", result);
        try {
          safeToast.success("Friend removed");
        } catch (toastErr) {
          console.error("Toast error:", toastErr);
        }
        await loadFriends();
      }
      
      setFriendToRemove(null);
    } catch (error) {
      console.error("Full error object:", error);
      const errorMessage = error.message || error.response?.data?.error || "Failed to remove friend";
      try {
        safeToast.error(String(errorMessage));
      } catch (toastErr) {
        console.error("Toast error:", toastErr);
        console.error("Original error:", errorMessage);
      }
      console.error("Error removing friend:", error);
      setFriendToRemove(null);
    }
  };

  const handleCancelRemoveFriend = () => {
    setRemoveFriendDialogOpen(false);
    setFriendToRemove(null);
  };

  const isRequestSent = (studentId) => {
    return sentRequests.some((req) => req.ReceiverId === studentId);
  };

  const getSentRequest = (studentId) => {
    return sentRequests.find((req) => req.ReceiverId === studentId);
  };

  const renderAlumniCard = (alum) => {
    const requestSent = isRequestSent(alum.StudentId);
    const sentReq = getSentRequest(alum.StudentId);
    const profileImageUrl = alum.ProfileImageUrl
      ? getFullUrl(alum.ProfileImageUrl)
      : null;

    return (
      <Card 
        key={alum.StudentId} 
        sx={{ 
          mb: 1.5, 
          boxShadow: 1,
          "&:hover": { boxShadow: 3 }
        }}
      >
        <CardContent sx={{ py: 1.5, px: 2, "&:last-child": { pb: 1.5 } }}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Avatar
              src={profileImageUrl}
              sx={{ width: 40, height: 40, cursor: "pointer" }}
              onClick={() => {
                setSelectedAlumni(alum);
                setProfileDialogOpen(true);
              }}
            />
            <Box flex={1} minWidth={0}>
              <Typography 
                variant="body1" 
                fontWeight={600}
                sx={{ fontSize: "0.9375rem" }}
                noWrap
              >
                {alum.FullName}
              </Typography>
              <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap" mt={0.25}>
                {alum.Batch && (
                  <Chip 
                    label={alum.Batch} 
                    size="small" 
                    sx={{ 
                      height: 20, 
                      fontSize: "0.7rem",
                      "& .MuiChip-label": { px: 0.75 }
                    }} 
                  />
                )}
                {alum.Location && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem" }}>
                    {alum.Location}
                  </Typography>
                )}
              </Box>
            </Box>
            <Box>
              {requestSent && sentReq ? (
                <IconButton
                  size="small"
                  sx={{ 
                    color: "error.main",
                    "&:hover": {
                      bgcolor: "error.light",
                      color: "error.dark"
                    }
                  }}
                  onClick={async () => {
                    console.log("Cancel button clicked, sentReq:", sentReq);
                    const requestId = sentReq.requestId || sentReq.RequestId;
                    console.log("requestId:", requestId);
                    if (requestId) {
                      await handleCancelRequest(requestId);
                    } else {
                      try {
                        safeToast.error("Request ID not found");
                      } catch (toastErr) {
                        console.error("Toast error:", toastErr);
                      }
                      console.error("sentReq object:", sentReq);
                    }
                  }}
                >
                  <CancelIcon sx={{ fontSize: "1.1rem" }} />
                </IconButton>
              ) : (
                <Button
                 
                  color="success"
                  size="small"
                  sx={{ 
                    minWidth: "auto",
                    px: 1.5,
                    py: 0.5,
                    fontSize: "0.8125rem",
                    textTransform: "none"
                  }}
                  onClick={() => handleSendRequest(alum.StudentId)}
                >
                  Add Friend
                </Button>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  };

  const renderPendingRequestCard = (request) => {
    const requesterImageUrl = request.RequesterProfileImageUrl || request.requesterProfileImageUrl;
    const profileImageUrl = requesterImageUrl
      ? requesterImageUrl.startsWith('http') 
        ? requesterImageUrl 
        : getFullUrl(requesterImageUrl)
      : null;

    return (
      <Card 
        key={request.RequestId} 
        sx={{ 
          mb: 1.5, 
          boxShadow: 1,
          "&:hover": { boxShadow: 3 }
        }}
      >
        <CardContent sx={{ py: 1.5, px: 2, "&:last-child": { pb: 1.5 } }}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Avatar src={profileImageUrl} sx={{ width: 48, height: 48 }} />
            <Box flex={1} minWidth={0}>
              <Typography 
                variant="body1" 
                fontWeight={600}
                sx={{ fontSize: "0.9375rem" }}
              >
                {request.RequesterName}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem", display: "block", mt: 0.25 }}>
                {(() => {
                  const requestedDate = request.RequestedDateTime || request.requestedDateTime;
                  if (requestedDate) {
                    try {
                      const date = new Date(requestedDate);
                      if (!isNaN(date.getTime())) {
                        return date.toLocaleDateString();
                      }
                    } catch (e) {
                      console.error("Date parsing error:", e);
                    }
                  }
                  return "Recently";
                })()}
              </Typography>
            </Box>
            <Box display="flex" gap={1}>
              <Button
               
                color="primary"
                size="small"
                sx={{ 
                  minWidth: "auto",
                  px: 2,
                  py: 0.5,
                  fontSize: "0.8125rem",
                  textTransform: "none",
                  fontWeight: 600
                }}
                onClick={async () => {
                  console.log("Accept button clicked, request:", request);
                  const requestId = request.requestId || request.RequestId;
                  console.log("requestId:", requestId);
                  if (requestId) {
                    await handleAcceptRequest(requestId);
                  } else {
                    safeToast.error("Request ID not found");
                    console.error("request object:", request);
                  }
                }}
              >
                Confirm
              </Button>
              <Button
               
                size="small"
                sx={{ 
                  minWidth: "auto",
                  px: 2,
                  py: 0.5,
                  fontSize: "0.8125rem",
                  textTransform: "none"
                }}
                onClick={async () => {
                  console.log("Delete button clicked, request:", request);
                  const requestId = request.requestId || request.RequestId;
                  console.log("requestId:", requestId);
                  if (requestId) {
                    await handleRejectRequest(requestId);
                  } else {
                    safeToast.error("Request ID not found");
                    console.error("request object:", request);
                  }
                }}
              >
                Delete
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  };

  // Helper function to check if a user is online
  const isUserOnline = useCallback((studentId) => {
    if (!onlineUsers || onlineUsers.length === 0) return false;
    return onlineUsers.some(user => {
      // Handle both camelCase and PascalCase property names
      const userId = user.studentId || user.StudentId;
      const userIdNum = typeof userId === 'string' ? parseInt(userId) : userId;
      const targetId = typeof studentId === 'string' ? parseInt(studentId) : studentId;
      return userIdNum === targetId;
    });
  }, [onlineUsers]);

  const renderFriendCard = (friend) => {
    // Handle both property name formats (PascalCase and camelCase)
    const requesterId = friend.RequesterId || friend.requesterId;
    const receiverId = friend.ReceiverId || friend.receiverId;
    
    const friendId = requesterId === currentStudentId ? receiverId : requesterId;
    const isFriendOnline = isUserOnline(friendId);
    
    // Debug log for unread counts
    if (friendId && unreadCounts[friendId] !== undefined) {
      console.log(`Friend ${friendId}: unread count = ${unreadCounts[friendId]}`);
    }
    
    const friendName = requesterId === currentStudentId 
      ? (friend.ReceiverName || friend.receiverName || "Unknown") 
      : (friend.RequesterName || friend.requesterName || "Unknown");
    const friendUserName =
      requesterId === currentStudentId 
        ? (friend.ReceiverUserName || friend.receiverUserName) 
        : (friend.RequesterUserName || friend.requesterUserName);
    
    // Get profile image URL - handle both property name formats
    const receiverImageUrl = friend.ReceiverProfileImageUrl || friend.receiverProfileImageUrl;
    const requesterImageUrl = friend.RequesterProfileImageUrl || friend.requesterProfileImageUrl;
    const friendImageUrl = requesterId === currentStudentId ? receiverImageUrl : requesterImageUrl;
    
    const profileImageUrl = friendImageUrl
      ? friendImageUrl.startsWith('http') 
        ? friendImageUrl 
        : getFullUrl(friendImageUrl)
      : null;

    // Debug logging
    console.log("renderFriendCard - friend object:", friend);
    console.log("renderFriendCard - friendId:", friendId, "friendName:", friendName);

    return (
      <Card 
        key={friend.RequestId} 
        sx={{ 
          mb: 1.5, 
          boxShadow: 1,
          "&:hover": { boxShadow: 3 }
        }}
      >
        <CardContent sx={{ py: 1.5, px: 2, "&:last-child": { pb: 1.5 } }}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Badge
              overlap="circular"
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              badgeContent={
                isFriendOnline ? (
                  <FiberManualRecordIcon
                    sx={{
                      fontSize: 12,
                      color: "success.main",
                      bgcolor: "background.paper",
                      borderRadius: "50%",
                    }}
                  />
                ) : null
              }
            >
              <Avatar 
                src={profileImageUrl} 
                sx={{ width: 40, height: 40, cursor: "pointer" }}
                onClick={() => {
                  // Could open profile dialog here if needed
                }}
              />
            </Badge>
            <Box flex={1} minWidth={0}>
              <Box display="flex" alignItems="center" gap={0.75} flexWrap="wrap">
                <Typography 
                  variant="body1" 
                  fontWeight={600}
                  sx={{ fontSize: "0.9375rem" }}
                  noWrap
                >
                  {friendName}
                </Typography>
                {isFriendOnline ? (
                  <Chip
                    label="Online"
                    size="small"
                    color="success"
                    sx={{
                      height: 18,
                      fontSize: "0.65rem",
                      "& .MuiChip-label": { px: 0.5 },
                    }}
                  />
                ) : (
                  <Chip
                    label="Offline"
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: "0.65rem",
                      bgcolor: "grey.400",
                      color: "white",
                      "& .MuiChip-label": { px: 0.5 },
                    }}
                  />
                )}
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem", display: "block", mt: 0.25 }}>
                {(() => {
                  const respondedDate = friend.RespondedDateTime || friend.respondedDateTime;
                  if (respondedDate) {
                    try {
                      const date = new Date(respondedDate);
                      if (!isNaN(date.getTime())) {
                        return `Friends since ${date.toLocaleDateString()}`;
                      }
                    } catch (e) {
                      console.error("Date parsing error:", e);
                    }
                  }
                  return "Friends";
                })()}
              </Typography>
            </Box>
            <Box display="flex" gap={1} alignItems="center">
              <Tooltip title="Start Chat">
                <Badge
                  badgeContent={
                    friendId && unreadCounts[friendId] && unreadCounts[friendId] > 0
                      ? unreadCounts[friendId] > 99 
                        ? "99+" 
                        : unreadCounts[friendId]
                      : null
                  }
                  color="error"
                  max={99}
                  sx={{
                    "& .MuiBadge-badge": {
                      fontSize: "0.65rem",
                      height: 18,
                      minWidth: 18,
                      padding: "0 4px",
                      fontWeight: 600,
                    },
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={async () => {
                      if (friendId && friendName) {
                        setSelectedFriendForChat({
                          friendId,
                          friendName,
                          profileImageUrl,
                          friendUserName
                        });
                        setChatOpen(true);
                        // Clear unread count when chat opens
                        setUnreadCounts((prev) => ({ ...prev, [friendId]: 0 }));
                        // Refresh unread count after a short delay to ensure it's cleared
                        setTimeout(() => {
                          updateUnreadCount(friendId);
                        }, 1000);
                      }
                    }}
                    sx={{
                      color: "success.main",
                      "&:hover": {
                        bgcolor: "success.light",
                        color: "success.dark"
                      }
                    }}
                  >
                    <ChatIcon sx={{ fontSize: "1.1rem" }} />
                  </IconButton>
                </Badge>
              </Tooltip>
              <Chip 
                icon={<CheckCircleIcon sx={{ fontSize: "1rem !important" }} />} 
                label="Friends"
                color="success"
                size="small"
                sx={{ 
                  fontSize: "0.75rem",
                  height: 24,
                  "& .MuiChip-label": { px: 1 }
                }}
              />
              <IconButton
                size="small"
                onClick={() => {
                  console.log("Delete button clicked - friendId:", friendId, "friendName:", friendName);
                  console.log("Current friend object:", friend);
                  if (friendId && friendName) {
                    handleRemoveFriendClick(friendId, friendName);
                  } else {
                    try {
                      safeToast.error("Unable to remove friend: Invalid friend data");
                    } catch (toastErr) {
                      console.error("Toast error:", toastErr);
                    }
                    console.error("Invalid friend data - friendId:", friendId, "friendName:", friendName);
                  }
                }}
                sx={{
                  color: "error.main",
                  "&:hover": {
                    bgcolor: "error.light",
                    color: "error.dark"
                  }
                }}
              >
                <DeleteIcon sx={{ fontSize: "1.1rem" }} />
              </IconButton>
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
        <Typography variant="h6" fontWeight={700} sx={{ fontSize: "1.25rem" }}>
          
        </Typography>
        <IconButton size="small" onClick={loadData}>
          <RefreshIcon fontSize="small" />
        </IconButton>
      </Box>

      <Tabs 
        value={tabValue} 
        onChange={(e, newValue) => setTabValue(newValue)} 
        sx={{ 
          mb: 1.5,
          borderBottom: 1,
          borderColor: "divider",
          minHeight: "auto",
          "& .MuiTab-root": {
            minHeight: 40,
            fontSize: "0.875rem",
            fontWeight: 600,
            textTransform: "none",
            px: 2
          }
        }}
      >
        <Tab
          label={
            <Box display="flex" alignItems="center" gap={0.5}>
              <span>All Alumni</span>
              {alumni.length > 0 && (
                <Chip 
                  label={alumni.length} 
                  size="small" 
                  sx={{ 
                    height: 18, 
                    fontSize: "0.7rem",
                    "& .MuiChip-label": { px: 0.5 }
                  }} 
                />
              )}
            </Box>
          }
        />
        <Tab
          label={
            <Box display="flex" alignItems="center" gap={0.5}>
              <span>Requests</span>
              {pendingRequests.length > 0 && (
                <Badge badgeContent={pendingRequests.length} color="error" sx={{ "& .MuiBadge-badge": { fontSize: "0.65rem", height: 16, minWidth: 16 } }}>
                  <Box sx={{ width: 16 }} />
                </Badge>
              )}
            </Box>
          }
        />
        <Tab
          label={
            <Box display="flex" alignItems="center" gap={0.5}>
              <span>Friends</span>
              {friends.length > 0 && (
                <Chip 
                  label={friends.length} 
                  size="small" 
                  color="success"
                  sx={{ 
                    height: 18, 
                    fontSize: "0.7rem",
                    "& .MuiChip-label": { px: 0.5 }
                  }} 
                />
              )}
            </Box>
          }
        />
      </Tabs>

      {tabValue === 0 && (
        <Box mb={1.5}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search alumni..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                handleSearch();
              }
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                fontSize: "0.875rem",
                height: 36
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: "1.1rem" }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : (
        <Box>
          {tabValue === 0 && (
            <Box>
              {alumni.length === 0 ? (
                <Typography color="text.secondary" textAlign="center" py={3} sx={{ fontSize: "0.875rem" }}>
                  No alumni found. Try adjusting your search.
                </Typography>
              ) : (
                alumni.map((alum) => renderAlumniCard(alum))
              )}
            </Box>
          )}

          {tabValue === 1 && (
            <Box>
              {pendingRequests.length === 0 && sentRequests.length === 0 && !loading && (
                <Typography color="text.secondary" textAlign="center" py={3} sx={{ fontSize: "0.875rem" }}>
                  No friend requests found.
                </Typography>
              )}
              
              {pendingRequests.length > 0 && (
                <>
                  <Typography variant="subtitle2" fontWeight={600} mb={1} sx={{ fontSize: "0.8125rem", color: "text.secondary" }}>
                    FRIEND REQUESTS
                  </Typography>
                  {pendingRequests.map((request) => renderPendingRequestCard(request))}
                </>
              )}

              {sentRequests.length > 0 && (
                <>
                  {pendingRequests.length > 0 && <Divider sx={{ my: 2 }} />}
                  <Typography variant="subtitle2" fontWeight={600} mb={1} sx={{ fontSize: "0.8125rem", color: "text.secondary" }}>
                    SENT REQUESTS
                  </Typography>
                  {sentRequests.map((request) => (
                  <Card 
                    key={request.RequestId} 
                    sx={{ 
                      mb: 1.5, 
                      boxShadow: 1,
                      "&:hover": { boxShadow: 3 }
                    }}
                  >
                    <CardContent sx={{ py: 1.5, px: 2, "&:last-child": { pb: 1.5 } }}>
                      <Box display="flex" alignItems="center" gap={1.5}>
                        <Avatar
                          src={(() => {
                            const receiverImageUrl = request.ReceiverProfileImageUrl || request.receiverProfileImageUrl;
                            if (receiverImageUrl) {
                              return receiverImageUrl.startsWith('http') 
                                ? receiverImageUrl 
                                : getFullUrl(receiverImageUrl);
                            }
                            return null;
                          })()}
                          sx={{ width: 40, height: 40 }}
                        />
                        <Box flex={1} minWidth={0}>
                          <Typography 
                            variant="body1" 
                            fontWeight={600}
                            sx={{ fontSize: "0.9375rem" }}
                            noWrap
                          >
                            {request.ReceiverName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem", display: "block", mt: 0.25 }}>
                            {(() => {
                              const requestedDate = request.RequestedDateTime || request.requestedDateTime;
                              if (requestedDate) {
                                try {
                                  const date = new Date(requestedDate);
                                  if (!isNaN(date.getTime())) {
                                    return `Sent ${date.toLocaleDateString()}`;
                                  }
                                } catch (e) {
                                  console.error("Date parsing error:", e);
                                }
                              }
                              return "Sent recently";
                            })()}
                          </Typography>
                        </Box>
                        <IconButton
                          size="small"
                          sx={{ 
                            color: "error.main",
                            "&:hover": {
                              bgcolor: "error.light",
                              color: "error.dark"
                            }
                          }}
                          onClick={async () => {
                            console.log("Cancel button clicked, request:", request);
                            const requestId = request.requestId || request.RequestId;
                            console.log("requestId:", requestId);
                            if (requestId) {
                              await handleCancelRequest(requestId);
                            } else {
                              try {
                                safeToast.error("Request ID not found");
                              } catch (toastErr) {
                                console.error("Toast error:", toastErr);
                              }
                              console.error("request object:", request);
                            }
                          }}
                        >
                          <CancelIcon sx={{ fontSize: "1.1rem" }} />
                        </IconButton>
                      </Box>
                    </CardContent>
                  </Card>
                  ))}
                </>
              )}
            </Box>
          )}

          {tabValue === 2 && (
            <Box>
              {friends.length === 0 ? (
                <Typography color="text.secondary" textAlign="center" py={3} sx={{ fontSize: "0.875rem" }}>
                  No friends yet. Start by adding alumni from the &quot;All Alumni&quot; tab!
                </Typography>
              ) : (
                friends.map((friend) => renderFriendCard(friend))
              )}
            </Box>
          )}
        </Box>
      )}

      {/* Profile Dialog */}
      <Dialog open={profileDialogOpen} onClose={() => setProfileDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedAlumni?.FullName}
          <IconButton
            onClick={() => setProfileDialogOpen(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CancelIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedAlumni && (
            <Box>
              <Box display="flex" justifyContent="center" mb={2}>
                <Avatar
                  src={
                    selectedAlumni.ProfileImageUrl
                      ? getFullUrl(selectedAlumni.ProfileImageUrl)
                      : null
                  }
                  sx={{ width: 120, height: 120 }}
                />
              </Box>
              <Typography variant="h6" textAlign="center" mb={2}>
                {selectedAlumni.FullName}
              </Typography>
              {selectedAlumni.UserName && (
                <Typography variant="body2" color="text.secondary" textAlign="center" mb={2}>
                  @{selectedAlumni.UserName}
                </Typography>
              )}
              <Grid container spacing={2} mt={1}>
                {selectedAlumni.Batch && (
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Batch
                    </Typography>
                    <Typography variant="body1">{selectedAlumni.Batch}</Typography>
                  </Grid>
                )}
                {selectedAlumni.Course && (
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Course
                    </Typography>
                    <Typography variant="body1">{selectedAlumni.Course}</Typography>
                  </Grid>
                )}
                {selectedAlumni.Location && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      Location
                    </Typography>
                    <Typography variant="body1">{selectedAlumni.Location}</Typography>
                  </Grid>
                )}
                {selectedAlumni.Organization && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      Organization
                    </Typography>
                    <Typography variant="body1">
                      {selectedAlumni.Organization}
                      {selectedAlumni.Designation && ` - ${selectedAlumni.Designation}`}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {selectedAlumni && (
            <>
              {isRequestSent(selectedAlumni.StudentId) ? (
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={async () => {
                    const req = getSentRequest(selectedAlumni.StudentId);
                    console.log("Cancel Request button clicked, req:", req);
                    if (req) {
                      const requestId = req.requestId || req.RequestId;
                      console.log("requestId:", requestId);
                      if (requestId) {
                        await handleCancelRequest(requestId);
                        setProfileDialogOpen(false);
                      } else {
                        safeToast.error("Request ID not found");
                        console.error("req object:", req);
                      }
                    } else {
                      safeToast.error("Request not found");
                    }
                  }}
                >
                  Cancel Request
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="success"
                  onClick={() => {
                    handleSendRequest(selectedAlumni.StudentId);
                    setProfileDialogOpen(false);
                  }}
                >
                  Add Friend
                </Button>
              )}
            </>
          )}
          <Button onClick={() => setProfileDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Remove Friend Confirmation Dialog */}
        <Dialog
          open={removeFriendDialogOpen}
          onClose={handleCancelRemoveFriend}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            Remove Friend
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ fontSize: "0.875rem", color: "text.secondary" }}>
              Are you sure you want to remove {friendToRemove?.friendName || "this friend"} as a friend?
            </Typography>
          </DialogContent>
          <DialogActions>
            <IconButton
              onClick={handleCancelRemoveFriend}
              sx={{
                color: "text.secondary",
                "&:hover": {
                  bgcolor: "action.hover",
                  color: "text.primary"
                }
              }}
            >
              <CancelIcon />
            </IconButton>
            <Button
              onClick={handleConfirmRemoveFriend}
              color="error"
              variant="contained"
            >
              Remove
            </Button>
          </DialogActions>
        </Dialog>

        {/* Private Chat Dialog */}
        {chatOpen && selectedFriendForChat && (
          <Dialog
            open={chatOpen}
            onClose={() => {
              setChatOpen(false);
              setSelectedFriendForChat(null);
            }}
            maxWidth="md"
            fullWidth
            fullScreen={isMobile}
            PaperProps={{
              sx: {
                height: isMobile ? "100vh" : "80vh",
                maxHeight: isMobile ? "100vh" : "80vh",
              }
            }}
          >
            <DialogContent sx={{ p: 0, height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
              <PrivateChat
                otherStudentId={selectedFriendForChat.friendId}
                otherStudentName={selectedFriendForChat.friendName}
                otherProfileImageUrl={selectedFriendForChat.profileImageUrl}
                onClose={() => {
                  setChatOpen(false);
                  setSelectedFriendForChat(null);
                }}
              />
            </DialogContent>
          </Dialog>
        )}
      </Box>
    );
  };
  
  export default FriendRequests;

